import React, { useEffect, useState } from 'react';

const CampaignStats = ({ campaign, contacts }) => {
  const [stats, setStats] = useState({
    statusBreakdown: {},
    conversionRate: 0,
    responseRate: 0,
    contactsPerDay: 0,
    daysRemaining: 0,
    projectedCompletion: 0,
    avgResponseTime: 0
  });

  useEffect(() => {
    calculateStats();
  }, [campaign, contacts]);

  const calculateStats = () => {
    if (!campaign || !contacts || contacts.length === 0) return;

    // Status breakdown
    const statusBreakdown = contacts.reduce((acc, contact) => {
      acc[contact.status] = (acc[contact.status] || 0) + 1;
      return acc;
    }, {});

    // Calculate rates
    const contacted = (statusBreakdown.contacted || 0) + 
                     (statusBreakdown.responded || 0) + 
                     (statusBreakdown.converted || 0);
    const responded = (statusBreakdown.responded || 0) + 
                     (statusBreakdown.converted || 0);
    const converted = statusBreakdown.converted || 0;
    
    const responseRate = contacted > 0 ? (responded / contacted) * 100 : 0;
    const conversionRate = contacted > 0 ? (converted / contacted) * 100 : 0;

    // Calculate days
    const today = new Date();
    const endDate = new Date(campaign.endDate);
    const startDate = new Date(campaign.startDate);
    const daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysPassed = Math.max(0, Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)));

    // Calculate contacts per day
    const contactsPerDay = daysPassed > 0 ? contacted / daysPassed : 0;

    // Project completion
    const projectedContacts = Math.round(contactsPerDay * totalDays);
    const projectedCompletion = campaign.targetCount > 0 
      ? Math.min(100, (projectedContacts / campaign.targetCount) * 100)
      : 0;

    // Average response time (dummy data for now)
    const avgResponseTime = 2.5; // days

    setStats({
      statusBreakdown,
      responseRate,
      conversionRate,
      contactsPerDay,
      daysRemaining,
      projectedCompletion,
      avgResponseTime
    });
  };

  // Format number with 1 decimal place
  const formatNumber = (num) => {
    return parseFloat(num).toFixed(1);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'responded': return 'bg-green-100 text-green-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="campaign-stats">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Conversion Rate */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Conversion Rate</h3>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-primary">{formatNumber(stats.conversionRate)}%</span>
            <span className="text-sm text-gray-500 mb-1">of contacted</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {stats.statusBreakdown.converted || 0} conversions from {contacts.length} contacts
          </p>
        </div>

        {/* Response Rate */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Response Rate</h3>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-primary">{formatNumber(stats.responseRate)}%</span>
            <span className="text-sm text-gray-500 mb-1">of contacted</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {(stats.statusBreakdown.responded || 0) + (stats.statusBreakdown.converted || 0)} responses received
          </p>
        </div>

        {/* Campaign Progress */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Campaign Progress</h3>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-primary">{formatNumber(stats.projectedCompletion)}%</span>
            <span className="text-sm text-gray-500 mb-1">projected completion</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {stats.daysRemaining} days remaining in campaign
          </p>
        </div>

        {/* Contact Rate */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Contact Rate</h3>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-primary">{formatNumber(stats.contactsPerDay)}</span>
            <span className="text-sm text-gray-500 mb-1">contacts per day</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Need {formatNumber((campaign.targetCount - campaign.completedCount) / stats.daysRemaining)} per day to reach target
          </p>
        </div>

        {/* Average Response Time */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Avg. Response Time</h3>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-primary">{stats.avgResponseTime}</span>
            <span className="text-sm text-gray-500 mb-1">days</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Average time to receive a response
          </p>
        </div>

        {/* Status Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Status Breakdown</h3>
          <div className="space-y-2 mt-4">
            {Object.entries(stats.statusBreakdown).map(([status, count]) => (
              <div key={status} className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className={`inline-block w-3 h-3 rounded-full mr-2 ${getStatusColor(status).split(' ')[0]}`}></span>
                  <span className="capitalize">{status}</span>
                </div>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Timeline */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Contact Status Timeline</h3>
        <div className="h-64 flex items-center justify-center">
          <p className="text-gray-500">Campaign statistics visualization will appear here</p>
        </div>
      </div>
    </div>
  );
};

export default CampaignStats;
